"use client";
import React, { useState } from "react";
import type { Props } from "@/components/pricing";
import { paddingMap } from "@/components/pricing";
import { getBackgroundStyle, getBtnVariant } from "@/lib/background";
import { Button } from "@/components/ui/button";

interface FAQItem {
  question: string;
  answer: string;
}

export default function PricingFaq({ data }: Props) {
  const {
    title,
    subtitle,
    plans = [],
    faq = [],
    background,
    padding = "xl",
  } = data || {};

  const bgStyle = getBackgroundStyle(background);

  return (
    <section
      className={`${paddingMap[padding] || "py-20 px-6"}`}
      style={bgStyle}
    >
      <div className="max-w-7xl mx-auto text-center">
        {title && (
          <h2 className="text-4xl font-bold mb-4 text-foreground">{title}</h2>
        )}
        {subtitle && (
          <p className="text-lg text-muted-foreground mb-10">{subtitle}</p>
        )}

        <div className="grid gap-6 sm:grid-cols-2 md:grid-cols-3 mb-16">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`border rounded-lg p-6 shadow-sm ${
                plan.highlighted
                  ? "border-primary bg-primary/5"
                  : "border-border bg-white"
              }`}
            >
              <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
              <p className="text-muted-foreground mb-4">{plan.description}</p>
              <div className="text-3xl font-bold mb-4">
                {plan.currency || "$"}
                {plan.price?.monthly ?? 0}
                <span className="text-sm text-muted-foreground"> /mo</span>
              </div>
              <ul className="mb-6 text-left text-sm space-y-2 text-foreground">
                {plan.features?.map((feature, i) => (
                  <li key={i}>• {feature}</li>
                ))}
              </ul>
              {plan.button?.href && plan.button?.text && (
                <Button
                  asChild
                  variant={getBtnVariant(plan.button.style)} // dùng mặc định nếu thiếu
                  className="px-5 py-2 text-sm font-medium"
                >
                  <a href={plan.button.href}>{plan.button.text}</a>
                </Button>
              )}
            </div>
          ))}
        </div>

        {faq.length > 0 && (
          <div className="max-w-3xl mx-auto text-left">
            <h3 className="text-2xl font-semibold mb-6 text-center">
              Frequently Asked Questions
            </h3>
            <div className="space-y-4">
              {faq.map((item, i) => (
                <FaqItem
                  key={i}
                  question={item.question}
                  answer={item.answer}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function FaqItem({ question, answer }: FAQItem) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border rounded-lg">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center p-4 font-medium text-left"
      >
        <span>{question}</span>
        <span>{open ? "−" : "+"}</span>
      </button>
      {open && <div className="p-4 pt-0 text-muted-foreground">{answer}</div>}
    </div>
  );
}
